import * as React from 'react'
import * as SliderPrimitive from '@radix-ui/react-slider'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

import { cn } from '@/lib/utils'

interface SliderProps
  extends React.ComponentProps<typeof SliderPrimitive.Root> {
  getTooltipLabel?: (value: number) => string
}

function Slider({
  className,
  defaultValue,
  value,
  min = 0,
  max = 100,
  getTooltipLabel,
  onValueChange,
  ...props
}: SliderProps) {
  const [isDragging, setIsDragging] = React.useState(false)

  const _values = React.useMemo(
    () =>
      Array.isArray(value)
        ? value
        : Array.isArray(defaultValue)
          ? defaultValue
          : [min, max],
    [value, defaultValue, min, max],
  )

  const handleValueChange = React.useCallback(
    (newValue: number[]) => {
      onValueChange?.(newValue)
    },
    [onValueChange],
  )

  // Ensure dragging state is cleared when component loses focus or interaction ends
  React.useEffect(() => {
    const handleGlobalPointerUp = () => {
      setIsDragging(false)
    }
    const handleGlobalMouseUp = () => {
      setIsDragging(false)
    }

    if (isDragging) {
      document.addEventListener('pointerup', handleGlobalPointerUp)
      document.addEventListener('mouseup', handleGlobalMouseUp)
      document.addEventListener('touchend', handleGlobalPointerUp)
    }

    return () => {
      document.removeEventListener('pointerup', handleGlobalPointerUp)
      document.removeEventListener('mouseup', handleGlobalMouseUp)
      document.removeEventListener('touchend', handleGlobalPointerUp)
    }
  }, [isDragging])

  const content = (
    <SliderPrimitive.Root
      data-slot="slider"
      defaultValue={defaultValue}
      value={value}
      min={min}
      max={max}
      onValueChange={handleValueChange}
      onPointerDown={() => setIsDragging(true)}
      onPointerUp={() => setIsDragging(false)}
      className={cn(
        'relative flex w-full touch-none items-center select-none data-[disabled]:opacity-50 data-[orientation=vertical]:h-full data-[orientation=vertical]:min-h-44 data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col',
        className,
      )}
      {...props}
    >
      <SliderPrimitive.Track
        data-slot="slider-track"
        className={cn(
          'bg-muted relative grow overflow-hidden rounded-full data-[orientation=horizontal]:h-1.5 data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-1.5',
        )}
      >
        <SliderPrimitive.Range
          data-slot="slider-range"
          className={cn(
            'bg-primary absolute data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full',
          )}
        />
      </SliderPrimitive.Track>
      {Array.from({ length: _values.length }, (_, index) => {
        const thumbElement = (
          <SliderPrimitive.Thumb
            data-slot="slider-thumb"
            key={index}
            className="border-primary bg-background ring-ring/50 block size-4 shrink-0 rounded-full border shadow-sm transition-[color,box-shadow] hover:ring-4 focus-visible:ring-4 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50"
          />
        )

        if (getTooltipLabel) {
          return (
            <Tooltip key={index} open={isDragging ? true : undefined}>
              <TooltipTrigger asChild>{thumbElement}</TooltipTrigger>
              <TooltipContent side="top">
                {getTooltipLabel(_values[index])}
              </TooltipContent>
            </Tooltip>
          )
        }

        return thumbElement
      })}
    </SliderPrimitive.Root>
  )

  return getTooltipLabel ? (
    <TooltipProvider>{content}</TooltipProvider>
  ) : (
    content
  )
}

export { Slider }
